import React, {useEffect, useMemo, useState} from "react";
import {Alert, Badge, Card, Col, Container, Form, Row, Spinner, Table} from "react-bootstrap";
import {publicStatusApi} from "../../api/publicStatus.js";
import bctcLogo from "../../images/BCTC_Logo_Horiz_small.png";

function StatusPage() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [municipalityFilter, setMunicipalityFilter] = useState("");

    useEffect(() => {
        const loadStatuses = async () => {
            setLoading(true);
            setError("");

            try {
                const response = await publicStatusApi.getProductStatuses();
                setRows(response);
            } catch (loadError) {
                console.error("Failed to load public product statuses", loadError);
                setError("Failed to load product status information.");
            } finally {
                setLoading(false);
            }
        };

        loadStatuses();
    }, []);

    const filteredRows = useMemo(() => {
        const normalizedMunicipalityFilter = municipalityFilter.trim().toLowerCase();

        return rows.filter((row) => {
            const matchesMunicipality = !normalizedMunicipalityFilter ||
                row.taxAuthorityName?.toLowerCase().includes(normalizedMunicipalityFilter);
            return matchesMunicipality;
        });
    }, [municipalityFilter, rows]);

    return (
        <Container className="py-4" style={{maxWidth: 1100}}>
            <Card className="border-0 shadow-sm overflow-hidden">
                <div
                    style={{
                        background: "linear-gradient(135deg, #f6fbff 0%, #eef5fb 55%, #e4eef8 100%)",
                        color: "#16324f",
                        padding: "2rem"
                    }}
                >
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                        <div className="d-flex align-items-center gap-3">
                            <div
                                style={{
                                    background: "rgba(255,255,255,0.82)",
                                    border: "1px solid #d5e2ee",
                                    borderRadius: "16px",
                                    padding: "0.9rem 1rem"
                                }}
                            >
                                <img src={bctcLogo} alt="BCTC logo" style={{height: 42, width: "auto", display: "block"}} />
                            </div>
                            <div>
                                <h1 style={{fontFamily: "Bitter, Georgia, serif", margin: "0.25rem 0 0.35rem"}}>System status</h1>
                                <div style={{maxWidth: 620, color: "#5d7288"}}>
                                    Current online status for all municipal products.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{padding: "1.5rem 1.5rem 0"}}>
                        <Card className="border-0 bg-light-subtle">
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={12}>
                                    <Form.Label>Filter by Municipality</Form.Label>
                                    <Form.Control
                                        value={municipalityFilter}
                                        onChange={(event) => setMunicipalityFilter(event.target.value)}
                                        placeholder="Start typing a municipality name"
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </div>

                <Card.Body className="p-4">
                    {error && <Alert variant="danger">{error}</Alert>}

                    {loading ? (
                        <div className="d-flex align-items-center py-4">
                            <Spinner animation="border" size="sm" className="me-2" />
                            <span>Loading status information...</span>
                        </div>
                    ) : (
                        <Table hover responsive className="mb-0">
                            <thead>
                                <tr>
                                    <th>Municipality</th>
                                    <th>Product</th>
                                    <th>Status</th>
                                    <th>Offline Reason</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRows.map((row) => (
                                    <tr key={row.productId}>
                                        <td>{row.taxAuthorityName}</td>
                                        <td>{row.productName}</td>
                                        <td>
                                            <span className={row.online ? "text-success fw-semibold" : "text-danger fw-semibold"}>
                                                {row.online ? "Online" : "Offline"}
                                            </span>
                                        </td>
                                        <td>{row.online ? "-" : (row.offlineReason || "-")}</td>
                                    </tr>
                                ))}
                                {!filteredRows.length && (
                                    <tr>
                                        <td colSpan={4} className="text-center text-muted py-4">No products match the current filters.</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
}

export default StatusPage;
